import { Component } from "@angular/core";
import { getValuesFromEnum } from "@app/class/Utilities";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { IFilterBarTab } from "@app/directives/_resourceFilters/filter-bar/filter-bar.models";
import { FilterBarService } from "@app/directives/_resourceFilters/filter-bar/filter-bar.service";
import { SortType } from "@app/enum/SortType";
import { TranslationService } from "@app/services/translation.service";
import { untilDestroyed } from "@ngneat/until-destroy";
import { Observable } from "rxjs";
import { finalize, tap } from "rxjs/operators";
import { ICameraApp, CameraApplicationState, CameraAppType } from "../../_models/ICameraApp";
import { CameraAppService } from "../../_services/camera-app.service";
import './application-manager.component.scss';

@Component({
    selector: 'application-manager',
    template: require('./application-manager.component.html')
})
export class ApplicationManagerComponent {

    cameraApps: ICameraApp[];
    selectedApp: ICameraApp;

    tableColumns: IVTableColumn<ICameraApp>[];
    tableRows: IVTableRow[];

    filterTabs: IFilterBarTab<ICameraApp>[];

    CameraApplicationState = CameraApplicationState;
    CameraAppType = CameraAppType;
    typesList: { translatedString: string, value: CameraAppType }[];
    statesList: { translatedString: string, value: CameraApplicationState }[];

    loading: boolean;
    errorMessage: string;

    constructor(
        private readonly cameraAppService: CameraAppService,
        private readonly filterBarService: FilterBarService,
        private readonly translationService: TranslationService,
        private readonly vTableService: VTableService
    ) { }

    ngOnInit(): void {
        this.createTabs();
        this.createTable();
        this.loadData();
    }

    archiveApp(confirm: boolean): void {
        if (!confirm) {
            return;
        }

        this.selectedApp.IsActive = false;

        this.updateApp();
    }

    unarchiveApp(): void {
        this.selectedApp.IsActive = true;

        this.updateApp();
    }

    updateApp(): void {
        this.clearError();

        this.saveApp(this.selectedApp);

        this.tableRows = this.vTableService.createTableRows(this.cameraApps, this.tableColumns);

        this.selectedApp = null;
    }

    filterAppByType(app: ICameraApp, type: { translatedString: string, value: CameraAppType }): boolean {
        return app.CameraAppType == type.value;
    }

    typeSearchPredicate(type: CameraAppType, searchText: string): boolean {
        const translatedType = this.translationService.tEnum(CameraAppType, "CameraAppType", type);
        return translatedType.toLowerCase().indexOf(searchText.toLowerCase()) >= 0;
    }

    filterAppByState(app: ICameraApp, type: { translatedString: string, value: CameraApplicationState }): boolean {
        return app.State == type.value;
    }

    stateSearchPredicate(state: CameraApplicationState, searchText: string): boolean {
        const translatedState = this.translationService.tEnum(CameraApplicationState, "CameraApplicationState", state);
        return translatedState.toLowerCase().indexOf(searchText.toLowerCase()) >= 0;
    }

    onRowClicked(tableRow: IVTableRow): void {
        this.selectedApp = tableRow.data;
    }

    addNewApp(): void {
        this.selectedApp = this.cameraAppService.createNewApp();
    }

    clearSelectedApp(): void {
        this.selectedApp = null;
        this.errorMessage = "";
    }

    onAppSaved(formData: FormData): void {
        this.loading = true;

        let saveMethod: Observable<ICameraApp> = this.cameraAppService.saveApp(formData);

        if (this.selectedApp.Id?.trim()) {
            saveMethod = this.cameraAppService.updateApp(this.selectedApp);
        }

        saveMethod.pipe(
            untilDestroyed(this)
        ).subscribe({
            next: app => {
                this.cameraApps.push(app);
                this.tableRows = this.vTableService.createTableRows(this.cameraApps, this.tableColumns);
            },
            complete: () => {
                this.clearSelectedApp();
                this.loading = false;
            },
            error: () => {
                this.errorMessage = this.translationService.t("AppManagement.ErrorSaving");
                this.loading = false;
            }
        });
    }

    onFormUpdated(): void {
        this.clearError();
    }

    //--

    private loadData(): void {
        this.cameraAppService.getApplicationFiles().pipe(
            untilDestroyed(this),
            tap(apps => {
                this.cameraApps = apps;
                this.createFilters();
                this.tableRows = this.vTableService.createTableRows(this.cameraApps, this.tableColumns);
            })
        ).subscribe();
    }

    private createTable(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createTableHeader({ tKey: 'RVLite.Version', sortType: SortType.Number, width: 150 }),
                createCellFunction: (app) => this.vTableService.createTableCell(app.Version)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'Common.Name' }),
                createCellFunction: (app) => this.vTableService.createTableCell(app.Name)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'Common.Description' }),
                createCellFunction: (app) => this.vTableService.createTableCell(app.Description)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'AppManagement.AppType' }),
                createCellFunction: (app) => this.vTableService.createEnumCell(CameraAppType, "CameraAppType", app.CameraAppType)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'AppManagement.State' }),
                createCellFunction: (app) => this.vTableService.createEnumCell(CameraApplicationState, "CameraApplicationState", app.State)
            }
        ]
    }

    private saveApp(app: ICameraApp): void {
        this.loading = true;

        this.cameraAppService.updateApp(app).pipe(
            untilDestroyed(this),
            finalize(() => {
                this.loading = false;
            })).subscribe();
    }

    private createFilters(): void {
        this.typesList = this.createFilterItems(CameraAppType, "CameraAppType");
        this.statesList = this.createFilterItems(CameraApplicationState, "CameraApplicationState");
    }

    private createFilterItems<T>(enumType: T, enumName: string): any {

        const enumValues = getValuesFromEnum(enumType);

        return enumValues.map(value => {
            return {
                translatedString: this.translationService.tEnum(enumType, enumName, value),
                value: value
            }
        });
    }

    private createTabs(): void {
        this.filterTabs = [];

        this.filterTabs.push(this.filterBarService.createTabDefinition<ICameraApp>("Common.Active", this.isActivePredicate));
        this.filterTabs.push(this.filterBarService.createTabDefinition<ICameraApp>("Common.Archived", this.isInactivePredicate));
    }

    private isActivePredicate = (app) => {
        return app.IsActive;
    }

    private isInactivePredicate = (app) => {
        return !app.IsActive;
    }

    private clearError(): void {
        this.errorMessage = "";
    }
}
